import { IStyles } from '../../../commonUtils/styleUtils';
import { NoContentCard } from '../NoContent.tsx';
import { pxToRem, pxTovW } from '../../../commonUtils/resizeUtils';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ReactNode } from 'react';
import { GIL, GIL_CELL, gilSize, gilTableText } from './gilTheme';

/** Cell padding of the Figma table: 16 on the left, 8 on the right. */
const cellPadding = {
  xs: `${pxToRem(8)} ${pxToRem(8)} ${pxToRem(8)} ${pxToRem(12)}`,
  md: `0 ${pxToRem(8)} 0 ${pxToRem(16)}`, lg: `0 ${pxTovW(8)} 0 ${pxTovW(16)}`,
};

/** The GIL design's table (Figma "Table"): grey header, hairline rows. */
const styles: IStyles = {
  tableContainer: {
    backgroundColor: '#FFFFFF',
    border: `1px solid ${GIL.tableBorder}`,
    borderRadius: gilSize(12, 10),
    overflowX: 'auto',
  },
  headerRow: {
    backgroundColor: GIL.tableHeadBg,
  },
  headerCell: {
    padding: cellPadding,
    height: gilSize(48, 40),
    borderColor: GIL.tableBorder,
  },
  cell: {
    padding: cellPadding,
    height: gilSize(64, 52),
    borderColor: GIL.tableBorder,
  },
};

export interface GilColumn<T> {
  key: string;
  header: string;
  render: (row: T, index: number) => ReactNode;
  align?: 'left' | 'center' | 'right';
  minWidth?: number;
}

interface IProps<T> {
  columns: GilColumn<T>[];
  rows: T[];
  rowKey: (row: T, index: number) => string | number;
  onRowClick?: (row: T) => void;
  emptyText?: string;
}

/** Report table; rows become a drill-down link when `onRowClick` is given. */
export const GilReportTable = <T,>({
  columns,
  rows,
  rowKey,
  onRowClick,
  emptyText = 'No Data available',
}: IProps<T>) => {
  if (rows.length === 0) {
    return <NoContentCard variant="info" icon="laboratory" text={emptyText} />;
  }
  return (
    <TableContainer sx={styles.tableContainer}>
      <Table>
        <TableHead>
          <TableRow sx={styles.headerRow}>
            {columns.map((column) => (
              <TableCell
                key={column.key}
                align={column.align ?? 'left'}
                sx={{ ...styles.headerCell, minWidth: column.minWidth }}
              >
                <Typography
                  noWrap
                  sx={gilTableText(
                    column.header === '#' ? 13 : 14,
                    600,
                    GIL.tableHead
                  )}
                >
                  {column.header}
                </Typography>
              </TableCell>
            ))}
            {onRowClick && <TableCell sx={styles.headerCell} />}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow
              key={rowKey(row, index)}
              hover={!!onRowClick}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              onKeyDown={
                onRowClick
                  ? (event) => {
                      if (event.key === 'Enter') onRowClick(row);
                    }
                  : undefined
              }
              sx={{
                cursor: onRowClick ? 'pointer' : 'default',
                backgroundColor: index % 2 ? GIL.zebra : '#FFFFFF',
                '&:last-child td': { borderBottom: 0 },
              }}
            >
              {columns.map((column) => {
                const content = column.render(row, index);
                return (
                  <TableCell
                    key={column.key}
                    align={column.align ?? 'left'}
                    sx={styles.cell}
                  >
                    {typeof content === 'string' ||
                    typeof content === 'number' ? (
                      <Typography noWrap sx={GIL_CELL.text}>
                        {content}
                      </Typography>
                    ) : (
                      content
                    )}
                  </TableCell>
                );
              })}
              {onRowClick && (
                <TableCell sx={styles.cell} align="center">
                  <ArrowForwardIosIcon
                    sx={{
                      width: { xs: pxToRem(10.04), md: pxToRem(14), lg: pxTovW(14) },
                      height: { xs: pxToRem(12.81), md: pxToRem(16), lg: pxTovW(16) },
                      color: GIL.green,
                    }}
                  />
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default GilReportTable;
